import { execFile } from 'child_process';
import { join } from 'path';
import { promisify } from 'util';

const execFileAsync = promisify(execFile);

/** tsup bundles everything into dist/index.js, so `..` is the package root. */
const PACKAGE_ROOT = join(__dirname, '..');

export interface SweepEntry {
  pid: number;
  name: string;
  ageSec: number;
}

export interface SweepResult {
  /** Processes matching the frozen-orphan signature (see the status line script). */
  candidates: SweepEntry[];
  /** Subset actually terminated (empty on a dry run). */
  killed: SweepEntry[];
  dryRun: boolean;
  /** Set on non-Windows platforms — nothing to sweep there. */
  unsupported?: boolean;
  /** Inspection failed (no PowerShell, WMI down, bad output…) — counts are unknown, not zero. */
  error?: string;
}

/** The status line script shipped in the package (the sweep engine lives there). */
export function statusLineScriptPath(): string {
  return join(PACKAGE_ROOT, 'templates', 'scripts', 'statusline', 'index.js');
}

function toEntries(value: unknown): SweepEntry[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (e): e is SweepEntry =>
      !!e && typeof e === 'object' && typeof (e as SweepEntry).pid === 'number' && typeof (e as SweepEntry).name === 'string',
  );
}

function parseSweepOutput(stdout: string, dryRun: boolean): SweepResult {
  const text = stdout.trim();
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start < 0 || end < start) return { candidates: [], killed: [], dryRun, error: 'no-json' };
  try {
    const parsed = JSON.parse(text.slice(start, end + 1)) as Partial<SweepResult>;
    return {
      candidates: toEntries(parsed.candidates),
      killed: toEntries(parsed.killed),
      dryRun,
      ...(parsed.unsupported ? { unsupported: true } : {}),
      ...(typeof parsed.error === 'string' ? { error: parsed.error } : {}),
    };
  } catch {
    return { candidates: [], killed: [], dryRun, error: 'parse' };
  }
}

/**
 * Reap the status line processes frozen at creation on Windows.
 *
 * Claude Code runs the status line through Git Bash and aborts a render still
 * running when the next refresh tick fires (`taskkill /T /F`). Cygwin — and
 * libuv for `detached` spawns — create their children CREATE_SUSPENDED and
 * resume them afterwards; a parent killed in that window leaves a `node.exe`
 * that never ran an instruction (1 thread, 0 CPU) and lives for ever, holding
 * a conhost and a `cygwin-console-helper` with it. The deployed script now
 * sweeps them every 5 minutes; this entry point runs the same engine on demand
 * (`ss doctor --fix`, `ss install`, `ss update`) for the backlog and the report.
 *
 * Runs the PACKAGED script, so it works even before the first deploy. Never
 * throws: an inspection failure is reported through `error`.
 */
export async function sweepStatusLineOrphans(options: { dryRun?: boolean } = {}): Promise<SweepResult> {
  const dryRun = options.dryRun ?? false;
  if (process.platform !== 'win32') return { candidates: [], killed: [], dryRun, unsupported: true };

  const args = [statusLineScriptPath(), '--sweep-orphans', '--json', ...(dryRun ? ['--dry-run'] : [])];
  try {
    const { stdout } = await execFileAsync(process.execPath, args, {
      timeout: 120000, // node start + a 60 s PowerShell budget: a paging machine is exactly when this runs
      windowsHide: true,
      encoding: 'utf8',
    });
    return parseSweepOutput(stdout, dryRun);
  } catch (error) {
    // Exit code 2 = the script reported an inspection error and still printed its JSON.
    const stdout = (error as { stdout?: string }).stdout;
    if (typeof stdout === 'string' && stdout.includes('{')) return parseSweepOutput(stdout, dryRun);
    return {
      candidates: [],
      killed: [],
      dryRun,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
